import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, Copy, Check, RefreshCw } from 'lucide-react';
import { supabase } from './supabase';
import { DataGrid, GridCol } from './ui/DataGrid';
import { Button } from './ui/arc/button';
import { Badge, BadgeTone } from './ui/arc/badge';
import { ChipGroup } from './ui/arc/chip-group';
import { OrderPeek, orderDisplayId } from './OrderPeek';
import { CABIFY_STATE_ES } from './CabifyBatch';

// "Envíos en curso" (02/11/2026): grilla de los envíos que están en manos de Cabify o por
// despachar — todo lo que NO está entregado ni cancelado. Por fila: pedido de Krilic (abre la
// orden en popup), cliente, etiqueta (1/2…), estado, ID de Cabify (copiable), link de seguimiento
// y fechas. Filtro por etapa (chips) + buscador del grid. Los devueltos / fallidos se muestran
// solo si son de los últimos 30 días (después ya no requieren acción acá).

const STAGES: { key: string; label: string; tone: BadgeTone; states: string[] }[] = [
  { key: 'sin_despachar', label: 'Sin despachar', tone: 'neutral', states: ['created', 'ready'] },
  { key: 'esperando', label: 'Esperando repartidor', tone: 'info', states: ['shipped', 'readytopickup', 'qualifiedforpickup', 'onroutetopickup', 'pickingup'] },
  { key: 'centro', label: 'En el centro de Cabify', tone: 'info', states: ['onroutetofinalhub', 'receivedonfinalhub', 'readytodispatch'] },
  { key: 'en_camino', label: 'En camino al cliente', tone: 'success', states: ['onroutetodelivery', 'intransit', 'delivering'] },
  { key: 'problema', label: 'Con problema', tone: 'danger', states: ['undelivered', 'onroutetoreturn', 'returning', 'incident', 'returned', 'returnedtoorigin', 'pickupfailed'] },
];
const stageOf = (st: string) => STAGES.find(s => s.states.includes(st)) || { key: 'otro', label: 'Otro', tone: 'neutral' as BadgeTone, states: [] };
const HIDDEN = ['delivered', 'deleted', 'requestercancel', 'internalcancel'];
const FINAL_PROBLEM = ['returned', 'returnedtoorigin', 'pickupfailed'];

interface Parcel { id: number; parcel_id: string; label: string; status: string; created_at: string; updated_at: string | null; tracking: string | null }
// Una fila por PEDIDO; si tiene varias etiquetas (bultos) cada una viaja por separado, así que
// estado / ID / seguimiento / fechas se muestran una por renglón dentro de la fila (02/11/2026).
interface Row { id: string; order_id: number | null; num: string; name: string; address: string; parcels: Parcel[] }

const fmt = (iso: string | null) => iso
  ? new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—';

// ID completo de Cabify (para soporte) + botón Copiar visible (03/11/2026: antes se veía cortado "c0792f8e…").
function CopyId({ id }: { id: string }) {
  const [ok, setOk] = useState(false);
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <code className="select-all font-mono text-[length:var(--text-xs)] text-soft">{id}</code>
      <button type="button" title="Copiar el ID completo" onClick={e => { e.stopPropagation(); navigator.clipboard?.writeText(id).then(() => { setOk(true); setTimeout(() => setOk(false), 1400); }); }}
        className={'inline-flex items-center gap-1 rounded-[var(--radius-control)] border border-line px-1.5 py-0.5 text-[length:var(--text-xs)] font-medium hover:bg-muted ' + (ok ? 'text-[var(--success)]' : 'text-ink')}>
        {ok ? <Check size={12} /> : <Copy size={12} />} {ok ? 'Copiado' : 'Copiar'}
      </button>
    </span>
  );
}

export function CabifyControl() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [stages, setStages] = useState<string[]>([]);
  const [peek, setPeek] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const since = new Date(Date.now() - 30 * 86400000).toISOString();
    const { data } = await supabase.from('cabify_deliveries')
      .select('id, order_id, parcel_id, external_id, status, created_at, updated_at, raw')
      .not('status', 'in', `(${HIDDEN.join(',')})`).not('parcel_id', 'like', 'demo%').not('external_id', 'ilike', '%demo%')
      .order('created_at', { ascending: false }).limit(500);
    const list = (data || []).filter((d: any) => !FINAL_PROBLEM.includes(d.status) || (d.updated_at || d.created_at) > since);
    const ids = Array.from(new Set(list.map((d: any) => d.order_id).filter(Boolean)));
    const info = new Map<number, any>();
    let all: any[] = list;
    if (ids.length) {
      const [{ data: os }, { data: sib }] = await Promise.all([
        supabase.from('orders').select('id, source, tiendanube_id, contact_name, delivery_address, status').in('id', ids),
        // las otras etiquetas del mismo pedido (aunque ya estén entregadas): viajan por separado
        supabase.from('cabify_deliveries').select('id, order_id, parcel_id, external_id, status, created_at, updated_at, raw')
          .in('order_id', ids).not('status', 'in', '(deleted,requestercancel,internalcancel)').not('parcel_id', 'like', 'demo%'),
      ]);
      (os || []).forEach((o: any) => info.set(o.id, o));
      const seen = new Set(list.map((d: any) => d.id));
      all = [...list, ...(sib || []).filter((d: any) => !seen.has(d.id))];
    }
    const groups = new Map<string, Row>();
    for (const d of all) {
      const key = d.order_id ? `o${d.order_id}` : `p${d.id}`;
      if (!groups.has(key)) {
        const o = d.order_id ? info.get(d.order_id) : null;
        const dropoff = d.raw?.created?.parcels?.[0]?.dropoff_info || d.raw?.created?.dropoff_info || null;
        groups.set(key, { id: key, order_id: d.order_id, parcels: [],
          num: o ? orderDisplayId(o) : (d.external_id || '(envío manual)'),
          name: o?.contact_name || dropoff?.name || '—', address: o?.delivery_address || dropoff?.addr || '' });
      }
      groups.get(key)!.parcels.push({ id: d.id, parcel_id: d.parcel_id, status: d.status, created_at: d.created_at, updated_at: d.updated_at,
        label: (String(d.external_id || '').match(/\d+\/\d+/) || [''])[0],
        tracking: d.raw?.shipped?.parcels?.[0]?.tracking_url || d.raw?.tracking_url || null });
    }
    // Etiquetas abandonadas: el pedido ya está entregado/cancelado (salió con otra etiqueta o por
    // otro medio) y esta quedó "esperando repartidor" desde hace más de 7 días → no es un envío en
    // curso, se oculta. Se ven en la lista de abajo ("Todos").
    const PRE_PICKUP = ['created', 'ready', 'shipped', 'readytopickup', 'qualifiedforpickup'];
    const weekAgo = Date.now() - 7 * 86400000;
    groups.forEach((g, k) => {
      const o = g.order_id ? info.get(g.order_id) : null;
      const open = g.parcels.filter(p => p.status !== 'delivered');
      const stale = open.every(p => PRE_PICKUP.includes(p.status) && Date.parse(p.created_at) < weekAgo);
      if ((o && ['entregado', 'cancelada'].includes(o.status) && stale) || (!o && stale && Date.parse(g.parcels[0].created_at) < weekAgo)) groups.delete(k);
    });
    const rowsOut = Array.from(groups.values());
    rowsOut.forEach(g => {
      g.parcels.sort((a, b) => (a.label || '0').localeCompare(b.label || '0', 'es', { numeric: true }));
      g.parcels.forEach((p, i) => { if (!p.label) p.label = `${i + 1}/${g.parcels.length}`; }); // etiqueta vieja sin "1/2"
    });
    rowsOut.sort((a, b) => Math.max(...b.parcels.map(p => Date.parse(p.created_at))) - Math.max(...a.parcels.map(p => Date.parse(p.created_at))));
    setRows(rowsOut);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try { await supabase.functions.invoke('cabify-logistics', { body: { action: 'sync_states' } }); await load(); }
    finally { setSyncing(false); }
  };

  // Los conteos son por ETIQUETA (cada bulto viaja por separado); un pedido aparece si alguna
  // de sus etiquetas está en las etapas elegidas.
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    rows.forEach(r => r.parcels.forEach(p => { if (p.status !== 'delivered') { const k = stageOf(p.status).key; c[k] = (c[k] || 0) + 1; } }));
    return c;
  }, [rows]);
  const shown = useMemo(() => stages.length ? rows.filter(r => r.parcels.some(p => stages.includes(stageOf(p.status).key))) : rows, [rows, stages]);

  // Una línea por etiqueta, todas de la misma altura para que las columnas queden alineadas.
  const lines = (r: Row, f: (p: Parcel) => React.ReactNode) => (
    <div className="flex flex-col">{r.parcels.map(p => <div key={p.id} className="flex h-7 items-center">{f(p)}</div>)}</div>
  );
  const firstStage = (r: Row) => Math.min(...r.parcels.map(p => p.status === 'delivered' ? 99 : STAGES.findIndex(s => s.key === stageOf(p.status).key)));

  const cols: GridCol<Row>[] = [
    { key: 'num', header: 'Pedido', width: 'w-28', sort: r => r.num,
      render: r => r.order_id
        ? <button type="button" className="font-semibold text-ink underline decoration-dotted underline-offset-2 hover:text-[var(--accent)]" title="Ver la orden"
            onClick={e => { e.stopPropagation(); setPeek(r.order_id); }}>{r.num}</button>
        : <span className="text-soft">{r.num}</span> },
    { key: 'name', header: 'Cliente', sort: r => r.name, render: r => <span className="block max-w-[14rem] truncate" title={r.address}>{r.name}</span> },
    { key: 'etiqueta', header: 'Etiqueta', width: 'w-20', sort: r => r.parcels.length,
      render: r => lines(r, p => <span className="tabular-nums text-soft">{p.label}</span>) },
    { key: 'estado', header: 'Estado', sort: r => firstStage(r),
      render: r => lines(r, p => <Badge size="sm" tone={p.status === 'delivered' ? 'success' : stageOf(p.status).tone}>{CABIFY_STATE_ES[p.status] || p.status}</Badge>) },
    { key: 'parcel', header: 'ID Cabify', hideOnMobile: true, sort: r => r.parcels.map(p => p.parcel_id).join(' '),
      render: r => lines(r, p => <CopyId id={p.parcel_id} />) },
    { key: 'track', header: 'Seguimiento', width: 'w-32',
      render: r => lines(r, p => p.tracking
        ? <a href={p.tracking} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[length:var(--text-sm)] font-medium text-[var(--accent)] hover:underline">Ver {r.parcels.length > 1 ? p.label : ''} <ExternalLink size={13} /></a>
        : <span className="text-[length:var(--text-sm)] text-faint" title="Cabify da el link cuando el envío se despacha">—</span>) },
    { key: 'creado', header: 'Etiqueta creada', width: 'w-32', hideOnMobile: true, sort: r => r.parcels[0]?.created_at,
      render: r => lines(r, p => <span className="whitespace-nowrap tabular-nums text-soft">{fmt(p.created_at)}</span>) },
    { key: 'cambio', header: 'Último cambio', width: 'w-32', hideOnMobile: true, sort: r => r.parcels.map(p => p.updated_at || p.created_at).sort().pop(),
      render: r => lines(r, p => <span className="whitespace-nowrap tabular-nums text-soft">{fmt(p.updated_at)}</span>) },
    { key: 'dir', header: 'Dirección', hideOnMobile: true, sort: r => r.address, render: r => <span className="block max-w-[18rem] truncate text-soft" title={r.address}>{r.address || '—'}</span> },
  ];

  return (
    <div className="tw mb-6 w-full min-w-0">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-[length:var(--text-lg)] font-semibold text-ink">Envíos en curso</h3>
          <p className="text-[length:var(--text-sm)] text-soft">Todo lo que no está entregado: por despachar, con Cabify o con problema. Tocá el nº de pedido para ver la orden.</p>
        </div>
        <Button variant="secondary" size="sm" loading={syncing} onClick={sync} title="Pide el estado actual de cada envío a Cabify">
          <RefreshCw size={14} /> Refrescar estados
        </Button>
      </div>
      <div className="mb-3">
        <ChipGroup label="Etapa" value={stages} onValueChange={setStages}
          options={STAGES.map(s => ({ value: s.key, label: `${s.label} · ${counts[s.key] || 0}` }))} />
      </div>
      <DataGrid<Row> rows={shown} cols={cols} loading={loading} dense pageSize={50} rowKey={r => r.id}
        searchPlaceholder="Buscar pedido, cliente, ID de Cabify…"
        emptyTitle="Nada en curso" emptyText="No hay envíos pendientes de entrega."
        filtersActive={stages.length > 0} onClearFilters={() => setStages([])} />
      <OrderPeek orderId={peek} onClose={() => setPeek(null)} />
    </div>
  );
}

export default CabifyControl;
